/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Divider,
  HStack,
  Heading,
  Link,
  SeeMore,
  Stack,
  Text,
} from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, useContext, useMemo, useState } from 'react';
import { PublicProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { MyNetworkContext } from 'v4/components/network/context';
import { MilestoneIcon } from '@mybridge/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';

export const MyProfileMilestone = forwardRef(({ ...props }, ref) => {
  const { milestones, publicProfile } = useContext(PublicProfileContext);
  const [showAll, setShowAll] = useState(false);
  const { isFriend } = useContext(MyNetworkContext);
  const entityHasMY = (ent) => ent?.month && ent?.year;
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((state) => state.userProfile);

  // Check if this is the user's own profile
  const isOwnProfile = publicProfile?.id === userProfileInfo?.id;

  // Filter milestones based on audience visibility
  const visibleMilestones = useMemo(() => {
    return milestones?.filter(ms => 
      ms?.audience === 1 || (ms?.audience === 2 && isFriend(publicProfile))
    ) ?? [];
  }, [milestones, isFriend, publicProfile]);

  // Check if all milestones are private (audience 2 or 3) and user is not a friend
  const allMilestonesPrivate = useMemo(() => {
    return milestones?.length > 0 && 
           milestones?.every(ms => ms?.audience === 2 || ms?.audience === 3) &&
           !isFriend(publicProfile) &&
           !isOwnProfile;
  }, [milestones, isFriend, publicProfile, isOwnProfile]);

  // If no milestones at all, return empty fragment
  if (!milestones?.length) {
    return <></>;
  }

  // Determine if we should show the heading - similar to experience and education components
  const shouldShowHeading = !allMilestonesPrivate && visibleMilestones?.length > 0;

  return (
    <>
      <Stack>
        {/* Only show heading if we have milestones to display */}
        {shouldShowHeading && (
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
                {t('profileMilestones.milestones') || "Milestones"}
              </Heading>
            </Stack>
          </HStack>
        )}
        
        {allMilestonesPrivate ? (
          <HStack>
            <MilestoneIcon />
            <Text color="#868585">
              {t('otherModules.noMilestonesToShow') || "No milestones to show"}
            </Text>
          </HStack>
        ) : visibleMilestones?.length ? (
          <>
            <Stack pt={4} spacing={6}>
              {visibleMilestones
                ?.filter?.((_, ind) => showAll || ind < 3)
                ?.map?.((ms, msIndex) => {
                  return (
                    <Stack key={msIndex}>
                      <HStack key={msIndex} alignItems="flex-start">
                        <MilestoneIcon />
                        <Stack spacing={0} flex={1}>
                          <Box>
                            <Heading size="sm">{ms?.title}</Heading>
                          </Box>
                          {entityHasMY(ms) ? (
                            <Box fontSize="sm">
                              <Text display="inline">
                                {moment()
                                  .set('M', parseInt(ms?.month ?? '1') - 1)
                                  ?.format?.('MMMM')}
                                ,&nbsp;
                                {moment()
                                  .set('Y', ms?.year)
                                  ?.format?.('YYYY')}{' '}
                              </Text>
                            </Box>
                          ) : (
                            ''
                          )}

                          <Stack py={2}>
                            {ms?.milestone ? (
                              <SeeMore text={ms?.milestone} />
                            ) : (
                              ''
                            )}
                          </Stack>
                        </Stack>
                      </HStack>
                      {visibleMilestones?.length > 1 &&
                      msIndex < visibleMilestones?.length - 1 ? (
                        <Divider />
                      ) : (
                        ''
                      )}
                    </Stack>
                  );
                })}
            </Stack>
            {!showAll && visibleMilestones?.length > 3 ? (
              <ShowAllLink onClick={(e) => setShowAll(true)} />
            ) : (
              ''
            )}
          </>
        ) : (
          <>
            <HStack>
              {isOwnProfile ? (
                
                <Link>{t('otherModules.noMilestonesToShow') || "No milestones to show"}</Link>

              ) : (
                <HStack>
                  <MilestoneIcon />
                  <Text color="#868585">
                    {t('otherModules.noMilestonesToShow') || "No milestones to show"}
                  </Text>
                </HStack>
              )}
            </HStack>
          </>
        )}
      </Stack>
    </>
  );
});
